import clsx from "clsx";
import { type FC } from "react";

import { ResponseType, useResponsesForPage } from "./responses";

import { ACCESS_ID_QUERY_PARAM, SUBMISSION_ID_QUERY_PARAM } from "components/constants";
import { AssessmentStatusIcon } from "components/materials/page/assessment/AssessmentStatusIcon";
import { NewSubmissionBadge } from "components/materials/page/assessment/NewSubmissionBadge";
import { getOfficialSubmission, getSubmissionsByAccessId } from "components/materials/page/helpers";
import { useIsAssessing } from "components/materials/section/assessment/hooks";
import type { PageType, SubmissionType } from "components/server-types";
import { Button } from "mds/components/Button";
import { IconText } from "mds/components/IconText";
import { Menu, MenuItem } from "mds/components/Menu";
import { useIsLgOrLarger } from "mds/hooks/use-responsive";
import {
  ChevronDownIcon,
  CircleExclamationIcon,
  PersonGroupIcon,
  PersonSingleIcon,
} from "mds/icons";
import { useAppSelector } from "store/index";
import {
  selectPageById,
  selectSortedSubmissionsByPageId,
  selectUsersInCurrentCourse,
} from "store/selectors";
import { useListSelector } from "store/store-hooks";
import { useFindItemBySearchParamId } from "utils/hooks/use-find-item-by-search-param-id";
import { isGroupPage } from "utils/page-utils";
import { useQueryParamsManager } from "utils/routing";

interface ResponseIconParams {
  accessId: string;
  page: PageType;
  submissions: SubmissionType[];
  assessmentsPublished: boolean;
  canAuthorCourse: boolean;
}

const ResponseIcon: FC<ResponseIconParams> = ({
  accessId,
  page,
  submissions,
  assessmentsPublished,
  canAuthorCourse,
}) => {
  const officialSubmission = getOfficialSubmission(submissions);

  if (submissions.length === 0) {
    return (
      <div className="text-orange">
        <CircleExclamationIcon />
      </div>
    );
  }

  if (officialSubmission) {
    return (
      <AssessmentStatusIcon
        accessId={accessId}
        assessmentsPublished={assessmentsPublished}
        canAuthorCourse={canAuthorCourse}
        className="text-green"
        page={page}
        iconOnly
      />
    );
  }

  return null;
};

interface StudentDropdownProps {
  canAuthorCourse: boolean;
  pageId: string;
}

export const StudentDropdown: FC<StudentDropdownProps> = ({ pageId, canAuthorCourse }) => {
  const { setQueryParams, deleteQueryParams } = useQueryParamsManager();
  const page = useAppSelector((s) => selectPageById(s, pageId));
  const assessmentsPublished = !!page?.assessments_published_at;
  const courseUsers = useListSelector(selectUsersInCurrentCourse);
  const submissions = useAppSelector((s) => selectSortedSubmissionsByPageId(s, pageId));
  const { responses } = useResponsesForPage(page);
  const isGroup = isGroupPage(page);
  const DisplayIcon = isGroup ? PersonGroupIcon : PersonSingleIcon;

  const isAssessing = useIsAssessing();
  const isLargeScreen = useIsLgOrLarger();
  const selectedResponse = useFindItemBySearchParamId(responses, ACCESS_ID_QUERY_PARAM, true);

  if (!selectedResponse || (isAssessing && isLargeScreen)) {
    return null;
  }

  const onSelectionChange = (item: ResponseType) => {
    if (item.id) {
      setQueryParams({ [ACCESS_ID_QUERY_PARAM]: item.id, [SUBMISSION_ID_QUERY_PARAM]: null });
    } else {
      deleteQueryParams([ACCESS_ID_QUERY_PARAM, SUBMISSION_ID_QUERY_PARAM]);
    }
  };

  const MenuButton = (
    <Button className="gap-1" kind="white" size="xs">
      <IconText
        className="gap-1"
        iconStart={<DisplayIcon className="group-data-[selected]/tab:text-blue" />}
        text={selectedResponse.title}
        hideIconOnSm
      />
      <ChevronDownIcon />
    </Button>
  );

  return (
    <div className="flex items-center">
      <Menu direction="bottom" menuButton={MenuButton} overflow="auto" position="initial">
        {responses.map((response) => {
          const selected = response.id === selectedResponse.id;
          const studentSubmissions = getSubmissionsByAccessId(
            response.id,
            courseUsers,
            submissions,
          );

          return (
            <MenuItem
              checked={selected}
              className={clsx({
                selected,
              })}
              key={response.id}
              onClick={() => onSelectionChange(response)}
            >
              <IconText
                className="gap-1"
                iconEnd={
                  <div className="flex items-center gap-1">
                    <ResponseIcon
                      accessId={response.id}
                      assessmentsPublished={assessmentsPublished}
                      canAuthorCourse={canAuthorCourse}
                      page={page}
                      submissions={studentSubmissions}
                    />

                    <NewSubmissionBadge sortedSubmissions={studentSubmissions} />
                  </div>
                }
                text={response.title}
              />
            </MenuItem>
          );
        })}
      </Menu>
    </div>
  );
};
